import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Menu, HardHat, FileText, Loader2, X, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { TopoBackground } from "@/components/TopoBackground";
import { useAdminAuth } from "@/context/AdminAuth";
import { MobileNav } from "@/components/MobileNav";
import { useSiteImages } from "@/lib/siteImages";
import { PROJECT_CATS } from "@/lib/constants";
import { getProjects } from "@/lib/api";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/83fd665c-12e1-46ec-b34b-7c468a7eac31/images/b2f6e5ab41253c81562fd816070a774e44fec66d2c9f35c709d8c5d3b4a40c46.jpeg";

const FILTERS = [
  ["All Projects", "all"],
  ["Construction", "construction"],
  ["Topographical", "topographical"],
  ["Grading & Slope", "grading"],
  ["Volume", "volume"],
  ["Building Layouts", "building"],
  ["As-Builts", "asbuilt"],
  ["Plan Production", "plan"],
  ["Legal", "legal"],
  ["Reports", "report"],
];

const SERVICE_OF = Object.fromEntries(PROJECT_CATS.map((p) => [p.cat, p.service]));

const DEFAULT_SECTIONS = {
  overview: "A summary of this project will be published once details are confirmed and approved.",
  requirement: "The client's surveying objectives will be described here, shared only with permission.",
  work_completed: "The field measurements and surveying tasks carried out will be listed here.",
  deliverables: "Plans, reports, and reference information delivered to the client will be summarized here.",
};

const Brand = ({ light }) => (
  <Link to="/" className="flex items-center gap-3">
    <img src="/data-earth-logo.webp" alt="Data Earth Surveying" className="h-10 w-10 object-contain" />
    <span className="leading-none">
      <span className={`block font-serif text-lg font-bold tracking-[0.08em] ${light ? "text-white" : "text-[#163323]"}`}>DATA EARTH SURVEYING</span>
      <span className="mt-0.5 block text-[9px] font-medium uppercase tracking-[0.28em] text-[#5a6660]">Land · People · A Brighter Tomorrow</span>
    </span>
  </Link>
);

export default function Projects() {
  const navigate = useNavigate();
  const { openModal } = useAdminAuth();
  const { img } = useSiteImages();
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [overrides, setOverrides] = useState({});

  useEffect(() => { getProjects().then((d) => setOverrides(d || {})).catch(() => {}); }, []);
  const projects = PROJECT_CATS.map((p) => ({ ...p, ...(overrides[p.cat] || {}) }));
  const anyContent = projects.some((p) => p.overview || p.requirement || p.work_completed || p.deliverables);

  const nav = [
    ["Home", "/"], ["Our Services", "/services"], ["About Us", "/about"],
    ["Projects", "/projects"], ["Request a Survey", "/request"],
    ["Contact", "/contact"],
  ];
  const shown = filter === "all" ? projects : projects.filter((p) => p.cat === filter);

  return (
    <div className="min-h-screen bg-[#f4f5f4]">
      {/* HEADER */}
      <header className="relative z-50 bg-[#163323] text-white">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Brand light />
          <nav className="hidden items-center gap-7 lg:flex">
            {nav.map(([label, href]) => {
              const active = label === "Projects";
              const cls = `relative text-sm font-medium transition-colors ${active ? "text-white" : "text-white/70 hover:text-white"}`;
              return href.startsWith("/") ? (
                <Link key={label} to={href} className={cls}>
                  {label}{active && <span className="absolute -bottom-1.5 left-0 h-0.5 w-full rounded bg-[#c8a452]" />}
                </Link>
              ) : <a key={label} href={href} className={cls}>{label}</a>;
            })}
          </nav>
          <MobileNav buttonClassName="rounded-full bg-[#c8a452] p-2 text-[#163323] lg:hidden" />
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0d2116]">
        <img src={img("projects_hero")} alt="Data Earth surveyor in the field" className="absolute inset-0 h-full w-full object-cover object-right" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d2116] via-[#163323]/85 to-transparent" />
        <TopoBackground opacity={0.08} />
        <div className="relative mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <h1 className="font-serif text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">Surveying Work in the Field</h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              A closer look at the measurements, planning support, and technical information behind successful
              land and construction projects across The Bahamas.
            </p>
          </motion.div>
          <div className="pointer-events-none absolute right-8 top-1/2 hidden -translate-y-1/2 text-right text-[11px] font-semibold uppercase leading-[2] tracking-[0.28em] text-white/60 lg:block">
            Measure<br />Plan<br /><span className="text-[#c8a452]">Build a Brighter<br />Bahamas</span>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="mx-auto max-w-[1400px] px-4 pt-10 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-center gap-2.5">
          {FILTERS.map(([label, key]) => (
            <button key={key} data-testid={`filter-${key}`} onClick={() => setFilter(key)}
              className={`rounded-md border px-4 py-2 text-sm font-medium transition-all ${
                filter === key ? "border-[#163323] bg-[#163323] text-white" : "border-[#d2dad5] bg-white text-[#3d4742] hover:border-[#c8a452]"
              }`}>
              {label}
            </button>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-4">
          <span className="h-px w-16 bg-[#c8a452]" />
          <span className="font-serif text-sm font-semibold uppercase tracking-[0.22em] text-[#8e6e23]">Project Portfolio Preview</span>
          <span className="h-px w-16 bg-[#c8a452]" />
        </div>
      </section>

      {/* GRID */}
      <section className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((p, i) => (
            <motion.div key={p.cat} data-testid={`project-card-${p.cat}`}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
              className="group overflow-hidden rounded-xl border border-[#e6eae7] bg-white shadow-sm">
              <div className="relative h-52 overflow-hidden">
                <img src={img(`project_${p.cat}`)} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="relative p-5">
                <TopoBackground stroke="#163323" opacity={0.04} />
                <div className="relative">
                  <h3 className="font-serif text-xl font-bold text-[#163323]">{p.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#5a6660]">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3 text-[#c8a452]" /> {p.island}</span>
                    <span>· {p.type}</span>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-[#5a6660]">{p.overview || "Project details coming soon"}</p>
                  <Button data-testid={`view-project-${p.cat}`} onClick={() => setDetail(p)}
                    className="mt-4 h-10 rounded-md bg-[#163323] px-4 text-sm font-semibold text-white hover:bg-[#0d2116]">
                    View Project <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PREPARED BAND */}
      {!anyContent && (
      <section className="border-y border-[#e6eae7] bg-[#eceeec]">
        <div className="mx-auto flex max-w-[1400px] items-center gap-5 px-4 py-8 sm:px-6 lg:px-8">
          <FileText className="hidden h-10 w-10 shrink-0 text-[#8e6e23] sm:block" strokeWidth={1.4} />
          <div className="text-center sm:text-left">
            <h2 className="font-serif text-2xl font-bold text-[#163323]">Our Project Portfolio Is Being Prepared</h2>
            <p className="mt-1 text-sm text-[#5a6660]">
              We are currently organizing selected fieldwork and verified project information for publication.
              Contact Data Earth Surveying to discuss your property or construction surveying needs.
            </p>
          </div>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#163323] text-white">
        <TopoBackground opacity={0.07} />
        <div className="relative mx-auto flex max-w-[1400px] flex-col gap-6 px-4 py-12 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div className="flex items-center gap-5">
            <HardHat className="hidden h-12 w-12 shrink-0 text-[#c8a452] sm:block" strokeWidth={1.4} />
            <div>
              <h2 className="font-serif text-2xl font-bold sm:text-3xl">Planning a Property or Construction Project?</h2>
              <p className="mt-1 text-sm text-white/70">Tell us what you need and receive a professional review of your surveying requirements.</p>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button data-testid="projects-request-btn" onClick={() => navigate("/request")}
              className="h-12 rounded-md bg-[#c1943a] px-7 font-semibold text-white hover:bg-[#a97f2c]">
              Request a Survey <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <a href="mailto:dataearthsurveying@gmail.com">
              <Button variant="outline" className="h-12 w-full rounded-md border-white/40 bg-transparent px-7 font-semibold text-white hover:bg-white/10 hover:text-white sm:w-auto">
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0d2116] text-white">
        <div className="mx-auto flex max-w-[1400px] flex-col items-center gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
          <div className="flex items-center gap-4">
            <Brand light />
            <span className="hidden h-8 w-px bg-white/20 sm:block" />
            <span className="hidden font-serif text-sm italic text-white/80 sm:block">Accurate. Reliable. Built for Your Project.</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <Link to="/" className="hover:text-[#c8a452]">Home</Link>
            <Link to="/services" className="hover:text-[#c8a452]">Our Services</Link>
            <Link to="/about" className="hover:text-[#c8a452]">About Us</Link>
            <Link to="/request" className="hover:text-[#c8a452]">Request a Survey</Link>
            <button onClick={openModal} data-testid="footer-admin-link" className="hover:text-[#c8a452]">Admin Portal</button>
          </div>
        </div>
      </footer>

      {/* DETAIL MODAL */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="project-detail" className="max-h-[92vh] max-w-2xl overflow-y-auto p-0">
          <DialogTitle className="sr-only">{detail?.title || "Project"}</DialogTitle>
          <DialogDescription className="sr-only">Project detail preview</DialogDescription>
          {detail && (
            <div>
              <div className="relative h-56 w-full overflow-hidden">
                <img src={img(`project_${detail.cat}`)} alt={detail.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d2116]/90 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5">
                  <h2 className="font-serif text-2xl font-bold text-white">{detail.title}</h2>
                  <div className="mt-1 flex flex-wrap gap-x-3 text-xs text-white/80">
                    <span>{detail.island}</span><span>· {detail.type}</span><span>· {SERVICE_OF[detail.cat]}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-5 p-6">
                {!(detail.overview || detail.requirement || detail.work_completed || detail.deliverables) && (
                  <div className="rounded-lg border border-[#e5d5ab] bg-[#f6f1e3] p-4 text-center">
                    <h3 className="font-serif text-lg font-bold text-[#163323]">Our Project Portfolio Is Being Prepared</h3>
                    <p className="mt-1 text-sm text-[#5a6660]">
                      We are currently organizing selected fieldwork and verified project information for publication.
                      Contact Data Earth Surveying to discuss your property or construction surveying needs.
                    </p>
                  </div>
                )}

                {[
                  ["Project Overview", detail.overview || DEFAULT_SECTIONS.overview],
                  ["Client's Requirement", detail.requirement || DEFAULT_SECTIONS.requirement],
                  ["Surveying Work Completed", detail.work_completed || DEFAULT_SECTIONS.work_completed],
                  ["Deliverables Provided", detail.deliverables || DEFAULT_SECTIONS.deliverables],
                ].map(([h, t]) => (
                  <div key={h}>
                    <h4 className="font-serif text-base font-semibold text-[#163323]">{h}</h4>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#5a6660]">{t}</p>
                  </div>
                ))}

                <div>
                  <p className="text-xs text-[#9aa5a0]">
                    To protect privacy, photos, addresses, and client details are published only with permission.
                  </p>
                </div>

                <div className="rounded-lg bg-[#f4f5f4] p-4">
                  <span className="text-xs uppercase tracking-wider text-[#8e6e23]">Related Surveying Service</span>
                  <div className="font-serif text-lg font-bold text-[#163323]">{SERVICE_OF[detail.cat]}</div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button data-testid="request-similar-btn"
                    onClick={() => navigate(`/request?service=${encodeURIComponent(SERVICE_OF[detail.cat])}`)}
                    className="h-12 flex-1 rounded-md bg-[#c1943a] font-semibold text-white hover:bg-[#a97f2c]">
                    Request a Similar Service <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <a href="mailto:dataearthsurveying@gmail.com" className="flex-1">
                    <Button variant="outline" className="h-12 w-full rounded-md border-[#d2dad5] font-semibold text-[#163323] hover:bg-[#f2f4f2]">
                      Contact Us
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
